import { CanAccess } from '@modules/common/components/CanAccess';
import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { NAV_PATHS } from '@modules/common/constants';
import { OtherUsersDashboardsList } from '@modules/dashboard/components/OtherUsersDashboardsList';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { useFetchDashboards } from '@modules/dashboard/hooks/useFetchDashboards';
import { SettingsRounded } from '@mui/icons-material';
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { useNavigate } from 'react-router-dom';
import { CurrentUserDashboardList } from '../CurrentUserDashboardList';
import { PinnedDashboardsListV2 } from '../PinnedDashboardsListV2';

interface DashboardListContainerProps {
  onClose: () => void;
}

export const DashboardListContainer = ({
  onClose,
}: DashboardListContainerProps) => {
  const navigate = useNavigate();

  const {
    dashboards: currentUserDashboards,
    loading: isCurrentUserDashboardLoading,
    hasMore: hasMoreCurrentUserDashboards,
  } = useFetchDashboards();

  const {
    dashboards: otherUserDashboards,
    loading: isOtherUserDashboardLoading,
    hasMore: hasMoreOtherUserDashboards,
  } = useFetchDashboards({
    shouldFetchOtherUserDashboards: true,
  });

  const createNewDashboard = () => {
    navigate(`/${NAV_PATHS.DASHBOARD}`);
    onClose();
    trackDashboardEvent({
      eventName: 'Create New Dashboard Clicked',
    });
  };

  const navigateToDashboardSettings = () => {
    navigate(`/${NAV_PATHS.DASHBOARD_SETTINGS}`);
    onClose();
    trackDashboardEvent({
      eventName: 'Dashboard Settings Button Clicked',
    });
  };

  const canManageTemplates = useIsAuthorized({
    resource: PermissionResource.Template,
    operation: PermissionOperation.Write,
  });

  return (
    <Stack p={3.5} spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={0.2}>
          <Typography variant="h6">Dashboards</Typography>
          {canManageTemplates && (
            <Tooltip arrow title="Dashboard Settings">
              <IconButton
                color="primary"
                size="small"
                onClick={navigateToDashboardSettings}
                aria-label="dashboard-settings-button"
              >
                <SettingsRounded />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <CanAccess
          operation={PermissionOperation.Write}
          resource={PermissionResource.Artefact}
        >
          <Button variant="outlined" onClick={createNewDashboard} size="small">
            + New Dashboard
          </Button>
        </CanAccess>
      </Stack>
      <Stack spacing={1}>
        <PinnedDashboardsListV2 onClose={onClose} />
        <CurrentUserDashboardList
          dashboards={currentUserDashboards}
          onClose={onClose}
          hasMore={hasMoreCurrentUserDashboards}
          isLoading={isCurrentUserDashboardLoading}
        />
        <OtherUsersDashboardsList
          dashboards={otherUserDashboards}
          onClose={onClose}
          hasMore={hasMoreOtherUserDashboards}
          isLoading={isOtherUserDashboardLoading}
        />
      </Stack>
    </Stack>
  );
};
